import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getJobPostings,
  getJobPosting,
  getJobPostingFilters,
  getJobPostingStats,
  getCrawlRuns,
  previewJobSearch,
  importJobPostings,
  type JobPostingQueryRequest,
  type JobPostingQueryResponse,
  type JobPosting,
  type JobPostingFiltersResponse,
  type JobPostingStats,
  type CrawlRun,
  type PreviewJobSearchRequest,
  type PreviewJobSearchResponse,
  type ImportJobPostingsRequest,
} from "@/services/api/job-postings";

export const jobPostingKeys = {
  all: ["job-postings"] as const,
  lists: () => [...jobPostingKeys.all, "list"] as const,
  list: (params: JobPostingQueryRequest) =>
    [...jobPostingKeys.lists(), params] as const,
  details: () => [...jobPostingKeys.all, "detail"] as const,
  detail: (id: number) => [...jobPostingKeys.details(), id] as const,
  filters: () => [...jobPostingKeys.all, "filters"] as const,
  stats: () => [...jobPostingKeys.all, "stats"] as const,
  crawlRuns: (count?: number) =>
    [...jobPostingKeys.all, "crawl-runs", count] as const,
};

export function useJobPostings(
  params: JobPostingQueryRequest,
  options?: Omit<
    UseQueryOptions<JobPostingQueryResponse, Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<JobPostingQueryResponse, Error>({
    queryKey: jobPostingKeys.list(params),
    queryFn: () => getJobPostings(params),
    ...options,
  });
}

export function useJobPosting(
  id: number,
  options?: Omit<UseQueryOptions<JobPosting, Error>, "queryKey" | "queryFn">
) {
  return useQuery<JobPosting, Error>({
    queryKey: jobPostingKeys.detail(id),
    queryFn: () => getJobPosting(id),
    enabled: id > 0,
    ...options,
  });
}

export function useJobPostingFilters(
  options?: Omit<
    UseQueryOptions<JobPostingFiltersResponse, Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<JobPostingFiltersResponse, Error>({
    queryKey: jobPostingKeys.filters(),
    queryFn: getJobPostingFilters,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useJobPostingStats(
  options?: Omit<
    UseQueryOptions<JobPostingStats, Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<JobPostingStats, Error>({
    queryKey: jobPostingKeys.stats(),
    queryFn: getJobPostingStats,
    ...options,
  });
}

const CRAWL_POLL_INTERVAL_MS = 5_000;

function hasRunningCrawl(runs: CrawlRun[] | undefined): boolean {
  if (!runs || runs.length === 0) return false;
  return runs.some((r) => r.status.toLowerCase() === "running");
}

export function useCrawlRuns(
  count: number = 20,
  options?: Omit<UseQueryOptions<CrawlRun[], Error>, "queryKey" | "queryFn">
) {
  return useQuery<CrawlRun[], Error>({
    queryKey: jobPostingKeys.crawlRuns(count),
    queryFn: () => getCrawlRuns(count),
    staleTime: 30 * 1000,
    refetchInterval: (query) =>
      hasRunningCrawl(query.state.data) ? CRAWL_POLL_INTERVAL_MS : false,
    ...options,
  });
}

export function usePreviewJobSearch() {
  return useMutation<PreviewJobSearchResponse, Error, PreviewJobSearchRequest>({
    mutationFn: previewJobSearch,
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to preview job search results",
      );
    },
  });
}

export function useImportJobPostings() {
  const queryClient = useQueryClient();

  return useMutation<CrawlRun, Error, ImportJobPostingsRequest>({
    mutationFn: importJobPostings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobPostingKeys.crawlRuns() });
      queryClient.invalidateQueries({ queryKey: jobPostingKeys.stats() });
      toast.success("Job posting import started");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to start job posting import",
      );
    },
  });
}

